import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "wouter";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useSearchCities } from "@/lib/api";
import { useAllQuizQuestions } from "@/data/quiz";
import tracking from "@/lib/tracking";
import {
  getQuizAudience,
  hasURLAudience,
  setQuizAudience,
  type QuizAudience,
} from "@/lib/quizAudience";
import OnboardingShell from "./OnboardingShell";
import DockLoginNote from "./DockLoginNote";
import { SCREEN_REGISTRY } from "./registry";
import {
  buildOnboardingFlow,
  clampToAccountGate,
  resolveResumeIndex,
  type OnboardingFlowConfig,
} from "./flow";
import { BEAT_HOLD_MS } from "./beats";
import { buildOnboardingContext } from "./trackingContext";
import type {
  FlowCopyContext,
  OnboardingAnswers,
  OnboardingRuntime,
  ScreenDef,
} from "./types";

interface OnboardingFlowProps {
  answers: OnboardingAnswers;
  onAnswer: (field: string, value: any) => void;
  /** Fired when the user finishes the last screen. */
  onFinish: (nav?: string) => void;
  /** Fired by an escape hatch: save, then leave for exactly this path. */
  onLeave: (path: string) => void;
  /** Fired the moment inline sign-in succeeds, before the welcome screen. */
  onAuthComplete?: () => void;
  /** Experiment hook — reorder, drop, or splice screens for a variant. */
  config?: OnboardingFlowConfig;
  /** Reported on every tracking event so variants are separable in PostHog. */
  variant?: string;
}

/**
 * Walks a `ScreenDef[]` and renders one screen at a time.
 *
 * The runner owns exactly three things: which screen is showing, whether the
 * user may continue from it, and the tracking. Everything visual belongs to
 * the screen components, and everything about *which* screens exist belongs
 * to `flow.ts` — which is what makes both the whole flow and any single
 * screen swappable without touching this file.
 */
export default function OnboardingFlow({
  answers,
  onAnswer,
  onFinish,
  onLeave,
  onAuthComplete,
  config,
  variant = "v3",
}: OnboardingFlowProps) {
  const { t, language } = useT();
  // `t` read through a ref wherever it feeds a memo. react-i18next returns a
  // new function on renders where nothing about the translations changed, so
  // any memo keyed on it misses every time — see the screen list below.
  const tRef = useRef(t);
  tRef.current = t;
  const { isSignedIn } = useAuth();
  const allQuestions = useAllQuizQuestions();
  // The age-range screen's options depend on the birthday and its photos on
  // the gender identity, so the flow is rebuilt when either changes — and
  // only then.
  // Read once, at mount. `?a=` can only arrive with the first load, and a
  // screen list that changed length underneath the runner would move every
  // step index after it — including the one the user is standing on.
  const [communityPinned] = useState(() => hasURLAudience());
  // Keyed on the language rather than on `t`: this builds twenty-one screens,
  // several of which translate a full set of option labels, and with `t` as
  // the dependency it ran on every render — including every tap of a
  // multi-select, where the answer changes but the screen list cannot.
  const screens = useMemo(
    () =>
      buildOnboardingFlow({
        t: tRef.current,
        birthday: answers.birthday,
        genderIdentity: answers.genderIdentity,
        isSignedIn,
        communityPinned,
        config,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      language,
      answers.birthday,
      answers.genderIdentity,
      isSignedIn,
      communityPinned,
      config,
    ],
  );

  // Beat progression for multi-beat screens, held here rather than inside the
  // screens so the shared dock button drives the same state a body tap does.
  const [beatIndex, setBeatIndex] = useState(0);
  // Flow position lives in the URL as `?step=<screenId>`.
  //
  // Not a convenience: it's the only place it can live that survives a full
  // page load. Sign-in happens mid-flow, and any auth method that navigates
  // away and back — redirect-based sign-in, an email link, the native shell
  // handing the WebView a token — destroys React state and refs. The URL and
  // localStorage are what come back. Answers are already in localStorage; this
  // is the other half.
  //
  // It doubles as the QA deep link (`?step=welcome`), which is how the tail of
  // the flow is reachable without signing up for a fresh account each time.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStep = searchParams.get("step");

  // Resolved during render, not in an effect. An effect would run after the
  // first commit, by which point the URL writer has already seeded `?step=`
  // from step 0 — and the sync would then dutifully follow that back to the
  // intro. Starting at the right index means there is nothing to race.
  //
  // `Onboarding` only mounts this once the draft has been hydrated, so the
  // answers this reads are the real ones.
  const [stepIndex, setStepIndex] = useState(() => {
    const fromUrl = urlStep ? screens.findIndex((s) => s.id === urlStep) : -1;
    const resolved =
      fromUrl >= 0 ? fromUrl : resolveResumeIndex(screens, answers, isSignedIn);
    return clampToAccountGate(screens, resolved, isSignedIn);
  });
  // Which step this session entered on, and whether the URL has been written
  // yet. Both exist to keep the history stack honest: see `back` and the URL
  // writer below.
  const entryStepRef = useRef<number | null>(null);
  // The step this session actually started on, which is the resumed one, not
  // necessarily zero. `back` compares against it to decide whether there is a
  // history entry of ours to unwind: getting it wrong on a resume meant the
  // chevron called `history.back()` with nothing behind it and left the app.
  if (entryStepRef.current === null) entryStepRef.current = stepIndex;
  const urlInitialised = useRef(false);
  // Set by `next`/`back`, cleared once the writer has recorded the move.
  // A step change has exactly one owner: either the runner moved and the URL
  // must follow, or the URL moved (back gesture, deep link, reload) and the
  // runner must follow. Without knowing which, the two effects below each
  // assume the other is stale and trade updates until React gives up.
  const runnerMoved = useRef(false);
  // The runner overrode the URL rather than followed it — see the gate clamp
  // in the sync effect. Like a stale URL, this is a correction and not a move,
  // so the writer replaces the entry instead of pushing one.
  const correctedRef = useRef(false);
  const [audience, setAudienceState] = useState<QuizAudience>(() => getQuizAudience());
  const exposureFiredRef = useRef(false);
  // Step ids that have already reported an answer, so the completion event
  // fires once per screen whether the user tapped an option (auto-advance) or
  // worked through the Continue dock.
  const completedRef = useRef<Set<string>>(new Set());

  // The URL drives the step, not just seeds it.
  //
  // This runs on every change to `?step=`, not once on mount, and that is the
  // half of browser-history support that isn't the writer below. A pushed
  // entry the runner ignores moves the address bar and nothing else: the back
  // gesture would appear to do nothing, then leave the flow entirely on the
  // second press. Reading it here is what makes Android's hardware back, iOS's
  // edge swipe and the desktop back arrow step through the flow.
  //
  // It doubles as the deep-link restore it replaced, and as the recovery path
  // after any auth round-trip that reloads the page mid-flow.
  useEffect(() => {
    if (!urlStep) return;
    const asked = screens.findIndex((s) => s.id === urlStep);
    if (asked < 0) return;
    const index = clampToAccountGate(screens, asked, isSignedIn);
    // Held back at the gate: the address bar names a screen this session may
    // not have, so the writer below has to correct it rather than leave the
    // URL describing somewhere the user isn't.
    if (index !== asked) correctedRef.current = true;
    setStepIndex((current) => (current === index ? current : index));
  }, [screens, urlStep, isSignedIn]);

  // Beats restart with the screen. Keyed on the step rather than reset inside
  // whatever moved it, so a step change from the URL — a back gesture, a
  // reload mid-flow — restarts them exactly as `next()` does.
  useEffect(() => {
    setBeatIndex(0);
  }, [stepIndex]);

  const screen: ScreenDef | undefined = screens[stepIndex];



  // City lookup for the handoff copy and the coming-soon dock. `selectedCity`
  // guarantees the picked city is in the response even when it's outside the
  // default page, and no `activeOnly` so an unlaunched pick resolves too.
  const { data: citiesResponse } = useSearchCities("", answers.location, 60);
  const pickedCity = useMemo(() => {
    if (!answers.location || !citiesResponse?.success) return undefined;
    return citiesResponse.data?.find((c) => c.id === answers.location);
  }, [citiesResponse, answers.location]);
  const cityName = pickedCity?.name ?? "";
  /** They picked somewhere we haven't launched yet. */
  const cityComingSoon = pickedCity ? !pickedCity.available : false;

  const labelFor = useCallback(
    (questionId: string, value: string) => {
      const q = allQuestions.find((qq) => qq.id === questionId);
      return q?.options?.find((o) => o.value === value)?.label ?? value;
    },
    [allQuestions],
  );

  const copy: FlowCopyContext = useMemo(
    () => ({ answers, cityName, labelFor, t }),
    [answers, cityName, labelFor, t],
  );

  // The community pick drives which photos the rest of the flow shows, so it
  // has to reach `getQuizAudience()` consumers (CardPhoto sources, the age
  // cards) as well as the saved answers.
  const setAnswer = useCallback(
    (field: string, value: any) => {
      if (field === "community" && value) {
        setQuizAudience(value as QuizAudience);
        setAudienceState(value as QuizAudience);
      }
      onAnswer(field, value);
    },
    [onAnswer],
  );

  // The screen is gone, so its answer has to arrive some other way: it is what
  // the matcher groups people by, and what the reveal and every photo pack
  // read. Mirrors the control stepper and the v3 stepper, which both do this
  // for the same reason. `onAnswer` rather than `setAnswer`: the audience is
  // already what this is copying from.
  useEffect(() => {
    if (!communityPinned) return;
    if (answers.community === audience) return;
    onAnswer("community", audience);
  }, [communityPinned, answers.community, audience, onAnswer]);

  // The step as the runner last committed it, plus any move it has claimed
  // since. See `moveFrom`.
  const stepRef = useRef(stepIndex);
  stepRef.current = stepIndex;

  /**
   * One move per screen, whoever asks for it.
   *
   * A move names the screen it leaves. Every way of leaving a screen is a
   * tap — the body of a handoff, an option on a single-select, the dock
   * button — and a tap can land twice: a double tap, a tap and the 260ms
   * auto-advance it scheduled, a tap on the body and one on the button. Each
   * of those used to be its own `setStepIndex(i => i + 1)`, and two of them
   * moved the flow two screens. The one in between was never shown, its
   * answer never given, and the server never stamped the profile complete —
   * which is how signed-in users ended up bounced back into onboarding for
   * good, with the flow resuming *after* the question it had skipped.
   *
   * So a move only counts from the screen the runner is actually on. The
   * second tap of the pair names a screen the flow has already left, and is
   * dropped. The claim is written to the ref immediately rather than waiting
   * for the render, so two taps that land in the same tick see it too.
   */
  const moveFrom = useCallback(
    (from: number, to: number) => {
      if (stepRef.current !== from) return;
      const target = Math.max(0, Math.min(screens.length - 1, to));
      if (target === from) return;
      stepRef.current = target;
      runnerMoved.current = true;
      setBeatIndex(0);
      setStepIndex(target);
    },
    [screens.length],
  );

  // Position, not kind, decides where the flow ends. Any screen can be last.
  const isLastScreen = stepIndex >= screens.length - 1;
  const advanceOrFinish = useCallback(() => {
    if (isLastScreen) {
      onFinish();
      return;
    }
    moveFrom(stepIndex, stepIndex + 1);
  }, [isLastScreen, onFinish, moveFrom, stepIndex]);

  const next = useCallback(() => {
    moveFrom(stepIndex, stepIndex + 1);
  }, [moveFrom, stepIndex]);

  // Unwinds the history stack rather than stepping independently of it: now
  // that every forward move pushes an entry, decrementing here would push
  // *another* one, and the browser's back would then walk forward through the
  // screens the user just reversed out of.
  //
  // Only when there is a flow entry to unwind. A deep link straight to a later
  // step wrote its URL with `replace`, so nothing of ours is behind it and
  // `history.back()` would leave the app entirely.
  const back = useCallback(() => {
    setBeatIndex(0);
    if (stepIndex > (entryStepRef.current ?? 0)) {
      window.history.back();
      return;
    }
    moveFrom(stepIndex, stepIndex - 1);
  }, [stepIndex, moveFrom]);

  // How many beats the current screen has. Handoffs declare theirs; the
  // account screen is a fixed two (the recap, then the sign-in options).
  const beatCount =
    screen?.kind === "statement"
      ? screen.beats.length
      : screen?.kind === "auth"
        ? 2
        : 1;

  const advanceBeat = useCallback(() => {
    if (beatIndex >= beatCount - 1) next();
    else setBeatIndex((i) => i + 1);
  }, [beatIndex, beatCount, next]);

  // Text always plays itself; a tap always means "next screen". Those are one
  // rule, not two: the moment a tap can mean either, the same gesture does
  // different things on screens that look identical, and the reader has to
  // find out which by trying it.
  //
  // Only intermediate beats run on the timer. The last one holds, because the
  // tap that leaves the screen is the reader's to make.
  const autoAdvanceBeats =
    screen?.kind === "statement" || screen?.kind === "auth";
  useEffect(() => {
    if (!autoAdvanceBeats) return;
    if (beatIndex >= beatCount - 1) return;
    const id = setTimeout(() => setBeatIndex((i) => i + 1), BEAT_HOLD_MS);
    return () => clearTimeout(id);
  }, [autoAdvanceBeats, beatIndex, beatCount]);

  /**
   * Who this session is, for every event below. Rebuilt as the answers land,
   * so a step viewed after the birthday question carries the age bracket and
   * one before it carries null — which is what makes a drop-off readable per
   * demographic rather than only in aggregate.
   */
  const trackingContext = useMemo(
    () =>
      buildOnboardingContext({
        variant,
        audience,
        language,
        answers,
        isSignedIn,
      }),
    [variant, audience, language, answers, isSignedIn],
  );

  const rt: OnboardingRuntime = useMemo(
    () => ({
      answers,
      setAnswer,
      next,
      back,
      beatIndex,
      advanceBeat,
      audience,
      copy,
      onAuthComplete,
      finish: onFinish,
      leave: onLeave,
      trackingContext,
    }),
    [
      answers,
      setAnswer,
      next,
      back,
      beatIndex,
      advanceBeat,
      audience,
      copy,
      onAuthComplete,
      onFinish,
      onLeave,
      trackingContext,
    ],
  );

  // One exposure marker per mount — the denominator for the whole experiment.
  // The context is read at first render and not tracked as a dependency: this
  // fires once, and the ref latch means a later answer must not re-fire it.
  const exposureContext = useRef(trackingContext);
  exposureContext.current = trackingContext;
  useEffect(() => {
    if (exposureFiredRef.current) return;
    exposureFiredRef.current = true;
    tracking.onboardingFlowExposed(exposureContext.current);
  }, []);

  // Mirror the current screen into the URL, pushing a history entry per step.
  //
  // This used to replace, so that the flow's chevron was the only back
  // affordance. That reasoning was wrong on the platform the flow actually
  // runs on: the browser's back gesture is not an alternative affordance the
  // product gets to opt out of, it is the one users reach for by reflex, and
  // with a replaced URL it discarded the entire flow in a single press.
  //
  // The first write is still a replace. Landing on `/onboarding` with no step
  // (or an unknown one) is a correction to the address bar rather than a move
  // the user made, and pushing there would put a junk entry behind them that
  // back has to walk through before it leaves.
  //
  // Guarded on inequality — writing the value it already has would loop, and
  // after a back gesture the URL is already correct, so this writes nothing.
  // Depends on the screen *id*, not the screen: `screens` is rebuilt whenever
  // `t` changes identity, so the object is new on every render and an effect
  // keyed on it runs every render. `setSearchParams` is asynchronous, so each
  // of those renders saw a URL that had not caught up yet and pushed again —
  // one step change stacked a run of identical entries, and back had to walk
  // every one of them.
  useEffect(() => {
    const screenId = screen?.id;
    if (!screenId) return;
    if (screenId === urlStep) {
      urlInitialised.current = true;
      return;
    }
    // Seeding a URL that has no usable step yet, versus recording a move.
    // Kept separate from `replace` below: a skipped screen also replaces, but
    // it is a move and must not take the seeding path's guards.
    const seeding = !urlInitialised.current;
    // The URL names a screen this flow no longer contains — the account step
    // removed the moment a session appeared, or a variant that dropped one.
    // Nobody "moved", but the address bar is wrong and the entry describes a
    // screen the user must not be sent back to.
    const urlStale = Boolean(urlStep) && !screens.some((sc) => sc.id === urlStep);
    const corrected = correctedRef.current;
    // The runner didn't move, so the URL did: a back gesture or a deep link
    // whose `setStepIndex` hasn't committed yet. `screen` is still the old one
    // here, and writing it would overwrite the destination with where the user
    // just came from — which the sync would then follow, forever.
    if (!seeding && !runnerMoved.current && !urlStale && !corrected) return;
    // Same, for the very first render of a deep link, where there is no
    // agreement yet to tell the two apart.
    if (seeding && !corrected && urlStep && screens.some((s) => s.id === urlStep))
      return;
    // Replacing, not pushing: a stale entry is not a place the user chose to
    // be, so back must skip over it rather than land on it.
    const replace = seeding || urlStale || corrected;
    runnerMoved.current = false;
    correctedRef.current = false;
    urlInitialised.current = true;
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set("step", screenId);
        return nextParams;
      },
      { replace },
    );
  }, [screen?.id, urlStep, screens, setSearchParams]);

  // Per-screen view event. This is the per-step drop-off denominator, so it
  // fires on every arrival, including backwards navigation.
  useEffect(() => {
    if (!screen) return;
    tracking.onboardingStepViewed({
      ...trackingContext,
      stepIndex,
      totalSteps: screens.length,
      stepId: screen.id,
      stepKind: screen.kind,
      act: screen.act,
    });
    // `trackingContext` is deliberately not a dependency: it changes with every
    // answer, and this event means "arrived at a step", not "answered one".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, stepIndex, screens.length]);

  // Answer captured — fires once per screen, on the tap that answers it.
  useEffect(() => {
    if (!screen) return;
    // The contact screen has two fields and no single `field` of its own;
    // either one filled in counts as answered, which is what the dashboard's
    // "arrivals vs answers" table reads for it.
    const field =
      screen.kind === "contact"
        ? answers.phone
          ? "phone"
          : "instagram"
        : screen.kind === "choice" || screen.kind === "birthday"
          ? screen.field
          : null;
    if (!field) return;
    if (completedRef.current.has(screen.id)) return;
    const value = answers[field];
    const has = Array.isArray(value) ? value.length > 0 : Boolean(value);
    if (!has) return;
    completedRef.current.add(screen.id);
    tracking.onboardingStepCompleted({
      ...trackingContext,
      stepId: screen.id,
      act: screen.act,
      field,
    });
  }, [screen, answers, trackingContext]);

  if (!screen) return null;

  const ScreenComponent = SCREEN_REGISTRY[screen.kind];

  // Progress runs to the account screen, not to the end: the welcome moment
  // sits after signup and shouldn't read as "still filling in forms".
  const progressDenominator =
    screens.findIndex((s) => s.kind === "auth") + 1 || screens.length;
  const progress = Math.min(100, (stepIndex / progressDenominator) * 100);

  const answered = (() => {
    if (screen.kind === "choice") {
      const value = answers[screen.field];
      if (!screen.required) return true;
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    }
    if (screen.kind === "birthday") return Boolean(answers[screen.field]);
    return true;
  })();

  /**
   * What the shared dock shows for this screen. The dock element itself is
   * permanent; only its label, note and enabled state change — which is why
   * the primary button never moves or double-renders between screens.
   *
   * Screens that advance on tap (city, single-select, the last beat of the
   * account screen) hide the button but keep its box, so nothing below it
   * shifts.
   */
  const dock: {
    label: string;
    onPress: () => void;
    hidden: boolean;
    note?: ReactNode;
    noteKey?: string;
    /** Tapping the screen body advances it. Drives the hint applied below. */
    tapAdvances?: boolean;
  } = (() => {
    switch (screen.kind) {
      case "intro":
        return {
          label: t(`${screen.copyKey}.cta`),
          onPress: next,
          hidden: false,
          note: <DockLoginNote copyKey={screen.copyKey} />,
          noteKey: "login",
        };
      case "statement":
        // Leaves the screen, exactly like a tap on the body — a handoff's
        // beats are on a timer, so there is no beat step left to drive. Press
        // it mid-sequence and you skip the rest, which is what someone
        // pressing a button during copy they are not reading intends.
        return {
          label: screen.ctaKey ? t(screen.ctaKey) : t("onboarding.continue"),
          onPress: next,
          hidden: !screen.ctaKey,
          tapAdvances: true,
        };
      case "auth":
        // No dock button and no hint on either beat. The recap plays itself
        // through on a timer (below), and once the sign-in options are up
        // they ARE the tap target — telling someone to "tap to continue"
        // next to three buttons just competes with them.
        return {
          label: t("onboarding.continue"),
          onPress: advanceBeat,
          hidden: true,
        };
      case "howItWorks":
        return { label: t("onboarding.continue"), onPress: next, hidden: false };
      // Both fields are optional at this position — the seat is booked and the
      // group chat lives in the app, so nothing downstream is blocked by a
      // missing number. The button always advances.
      case "contact":
        return { label: t("onboarding.continue"), onPress: next, hidden: false };
      // Picking a night advances the screen itself, and the "choose later"
      // link handles anyone who doesn't. A Continue here would be a third way
      // to do the same thing, and on a screen with an empty list it would be
      // the only button — reading as if there were something to confirm.
      case "events":
        return {
          label: t("onboarding.continue"),
          onPress: advanceOrFinish,
          hidden: true,
        };
      // Pressing a plan opens checkout for that plan, so there is nothing for
      // a Continue to confirm — see PlansScreen. It would also have to mean
      // something on its own ("continue with which plan?"), and every answer
      // to that is a second decision about one already made.
      case "plans":
        return {
          label: t(`${screen.copyKey}.cta`),
          onPress: advanceOrFinish,
          hidden: true,
        };
      // The last screen in the list is what ends the flow — see `isLastScreen`
      // below. Wiring "finish" to a particular *kind* broke the moment the
      // order changed: `welcome` moved ahead of `plans` and kept calling
      // `onFinish`, so its CTA left the flow and the offer behind it became
      // unreachable, while `plans` inherited the last slot with an `onPress`
      // that clamps and does nothing.
      case "welcome":
        return {
          label: t(`${screen.copyKey}.cta`),
          onPress: advanceOrFinish,
          hidden: false,
        };
      case "city":
        // Nothing to confirm for a city we serve — the tap already advanced.
        // A coming-soon pick gets the button and the explanation instead.
        return {
          label: t("onboarding.continue"),
          onPress: next,
          hidden: !cityComingSoon,
          note: cityComingSoon
            ? t("quiz.citySelect.comingSoonDescription")
            : undefined,
          noteKey: cityComingSoon ? "coming-soon" : undefined,
        };
      case "choice": {
        // Single-select advances on tap, so it shows its reassurance line
        // where the button would be instead of a button.
        const tapAdvances = !screen.multi && screen.autoAdvance !== false;
        return {
          label: t("onboarding.continue"),
          onPress: next,
          hidden: tapAdvances,
          note:
            tapAdvances && screen.footnoteKey ? t(screen.footnoteKey) : undefined,
          noteKey: tapAdvances && screen.footnoteKey ? screen.id : undefined,
        };
      }
      default:
        return { label: t("onboarding.continue"), onPress: next, hidden: false };
    }
  })();

  // The "tap to continue" hint is applied in exactly one place, and only
  // where there is nothing to press. Screens were setting it themselves and
  // drifted: the account screen ended up showing the hint, a dock note AND a
  // live Continue button all at once. Deriving it from `hidden` makes the
  // rule structural — a screen that gains a button loses the hint for free.
  if (dock.tapAdvances && dock.hidden && !dock.note) {
    dock.note = t("onboarding.tapToContinue");
    dock.noteKey = "tap";
  }

  return (
    <OnboardingShell
      stepKey={screen.id}
      progress={progress}
      showProgress={screen.chrome !== "bare"}
      canGoBack={stepIndex > 0 && screen.kind !== "welcome"}
      onBack={back}
      glow={
        screen.kind === "statement" && screen.visual === "pulse"
          ? "none"
          : screen.act === "identity"
            ? "secondary"
            : screen.act === "personality"
              ? "accent"
              : "primary"
      }
      glowPosition={stepIndex % 2 === 0 ? "top-right" : "bottom-left"}
      // The radar handoff brings the design's own backdrop, so the generic
      // corner glow steps aside rather than competing with the bloom.
      backdrop={
        screen.kind === "statement" && screen.visual === "pulse" ? "grid" : "none"
      }
      continueLabel={dock.label}
      canContinue={answered}
      onContinue={dock.onPress}
      hideContinue={dock.hidden}
      dockNote={dock.note}
      dockNoteKey={dock.noteKey}
    >
      <ScreenComponent def={screen} rt={rt} />
    </OnboardingShell>
  );
}
